import { Component, EventListener, findComponents } from '@gondel/core';
import { GondelJqueryComponent } from '@gondel/plugin-jquery';
import { dispatchCustomEvent, EventNames } from '@swisslife/shared-ui/src/utils/custom-event/js/custom-event';
import { ListFilter } from '@swisslife/patterns-base/src/molecules/list-filter/js/list-filter';
import '@swisslife/patterns-base/src/molecules/search-box/js/search-box';
import ajax from '@swisslife/shared-ui/src/utils/ajax/js/ajax';

// add your states here, e.g. Hidden: 'state-o-search-result--hidden',
enum States {
	MoreVisible = 'state-o-search-result__more--visible',
	LoaderVisible = 'state-a-loader--visible',
	Error = 'state-o-search-result--error',
}

// add your selector here, e.g. Element = '.js-o-search-result',
enum Selectors {
	ListOutlet = '.js-o-search-result__list-outlet',
	CountOutlet = '.js-o-search-result__count-outlet',
	More = '.js-o-search-result__more',
	MoreLink = '.js-o-search-result__more-link',
	Loader = '.js-a-loader',
}

// The @Component decorator will connect the class with `data-g-name="SearchResult"` elements.
@Component('SearchResult')
export class SearchResult extends GondelJqueryComponent {
	private listFilter: ListFilter;
	private $loader: JQuery<HTMLElement>;
	private $more: JQuery<HTMLElement>;
	private $moreLink: JQuery<HTMLElement>;
	private $countOutlet: JQuery<HTMLElement>;
	private $listOutlet: JQuery<HTMLElement>;
	private resultCount: number;
	private searchTerm: string;
	private searchCategory: string;

	public start() {
		const listFilterComponents = findComponents<ListFilter>(this._ctx, 'ListFilter');

		if (listFilterComponents.length > 0) {
			this.listFilter = listFilterComponents[0];
			this.listFilter.registerCallback(this.handleClickTab);
		}

		this.$loader = $(Selectors.Loader, this.$ctx);
		this.$more = $(Selectors.More, this.$ctx);
		this.$moreLink = $(Selectors.MoreLink, this.$ctx);
		this.$countOutlet = $(Selectors.CountOutlet, this.$ctx);
		this.$listOutlet = $(Selectors.ListOutlet, this.$ctx);
	}

	public sync() {
		const $item = this.getfirstListItem();
		this.saveDataAttributesFromItem($item);
		this.dispatchSearchSubmitEvent();
	}

	private saveDataAttributesFromItem = ($item: JQuery<HTMLElement>) => {
		this.resultCount = $item.data('result-count') || 0;
		this.searchTerm = $item.data('search-term') || '';
		this.searchCategory = $item.data('search-category') || '';
	};

	private getfirstListItem = (fragment?: string): JQuery<HTMLElement> => {
		const $list = fragment ? $(`<ul>${fragment}</ul>`) : this.$listOutlet;
		return $list.find('li').eq(0);
	};

	private dispatchSearchSubmitEvent() {
		if (this.searchTerm === '') {
			return;
		}
		dispatchCustomEvent(this.$ctx, EventNames.SearchSubmit, {
			searchTerm: this.searchTerm,
			resultCount: this.resultCount,
			searchCategory: this.searchCategory,
		});
	}

	private setUpdating = () => {
		this.$loader.addClass(States.LoaderVisible);
	};

	private setDone = () => {
		this.$loader.removeClass(States.LoaderVisible);
	};

	private setError = () => {
		this.$ctx.addClass(States.Error);
	};

	private wait(delay: number) {
		return new Promise((recieve) => setTimeout(recieve, delay));
	}

	private updateMoreLinkByFragment = ($firstItem: JQuery<HTMLElement>) => {
		const newCount: string = $firstItem.data('result-count');
		const moreTitle: string = $firstItem.data('more-title');
		const moreUrl = $firstItem.data('more-url');
		const moreFragmentUrl = $firstItem.data('more-fragment-url');

		const hasMoreContent = Boolean(moreUrl) && Boolean(moreFragmentUrl);
		this.$more.toggleClass(States.MoreVisible, hasMoreContent);

		this.$moreLink.attr('href', moreUrl);
		this.$moreLink.data('fragment-url', moreFragmentUrl);
		this.$countOutlet.html(newCount);

		return {
			moreTitle,
			moreUrl,
			moreFragmentUrl,
		};
	};

	@EventListener('click', Selectors.MoreLink)
	private async _handleClickMore(event: MouseEvent) {
		event.preventDefault();

		try {
			this.setUpdating();
			// fetch but wait for 150 ms minimum (prevent too fast update)
			const fragment = (await Promise.all([
				ajax<string>(this.$moreLink.data('fragment-url')),
				this.wait(150),
			]))[0];
			const $firstItem = this.getfirstListItem(fragment);
			const { moreUrl, moreTitle } = this.updateMoreLinkByFragment($firstItem);
			// update uri and page title
			history.pushState({}, moreTitle, moreUrl);
			// set browser tab title even if it's not supported by pushState
			$(document).prop('title', moreTitle);
			this.$listOutlet.append(fragment);
			this.setDone();
		} catch (error) {
			const statusCode = error.status ? error.status.toString() : 'n/a';
			dispatchCustomEvent(this.$ctx, EventNames.NetworkError, { statusCode });
			this.setDone();
			this.setError();
		}
	}

	private handleClickTab = async (
		filterType: string,
		filterItem: { itemKey: string; itemName: string },
		isSelected: boolean,
		$clickedItem: JQuery<HTMLElement>
	) => {
		try {
			this.setUpdating();
			// update uri and page title
			history.pushState({}, $clickedItem.data('title'), $clickedItem.attr('href'));
			// set browser tab title even if it's not supported by pushState
			$(document).prop('title', $clickedItem.data('title'));
			// fetch but wait for 150 ms minimum (prevent too fast update)
			const fragment = (await Promise.all([ajax<string>($clickedItem.data('fragment-url')), this.wait(150)]))[0];
			const $firstItem = this.getfirstListItem(fragment);
			this.updateMoreLinkByFragment($firstItem);
			this.$listOutlet.html(fragment);
			this.saveDataAttributesFromItem($firstItem);
			this.dispatchSearchSubmitEvent();
			this.setDone();
		} catch (error) {
			const statusCode = error.status ? error.status.toString() : 'n/a';
			dispatchCustomEvent(this.$ctx, EventNames.NetworkError, { statusCode });
			this.setDone();
			this.setError();
		}
	};
}
